import React, { useState } from "react";
import {
    Container, Row, Col, Card, CardBody, Button, Input,
    FormGroup, Label, Spinner, Badge
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { APIClient } from "../../helpers/api_helper";
import { toast } from "react-toastify";

const api = new APIClient();

const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

interface Bill {
    id: number;
    month: number;
    year: number;
    totalAmount: number;
    penalty: number;
    unitCount: number;
    units: { unitNo: string }[];
    status: string;
}

const QuickPay: React.FC = () => {
    const navigate = useNavigate();
    
    // Step management
    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [loading, setLoading] = useState(false);
    
    // Form data
    const [phone, setPhone] = useState("");
    const [otp, setOtp] = useState("");
    const [maskedEmail, setMaskedEmail] = useState("");
    const [resendTimer, setResendTimer] = useState(0);
    
    // Bills data
    const [token, setToken] = useState("");
    const [resident, setResident] = useState<any>(null);
    const [bills, setBills] = useState<Bill[]>([]);
    const [paying, setPaying] = useState(false);

    /* ── Step 1: Send OTP ── */
    const handleSendOTP = async () => {
        if (phone.length !== 10) {
            toast.warning("Enter valid 10-digit phone");
            return;
        }

        setLoading(true);
        try {
            const res: any = await api.create("/api/quick-pay/send-otp", { phone });
            if (res?.success) {
                setMaskedEmail(res.data.email);
                toast.success("OTP sent to your email!");
                setStep(2);
                startResendTimer();
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Failed to send OTP");
        } finally {
            setLoading(false);
        }
    };

    /* ── Resend Timer ── */
    const startResendTimer = () => {
        setResendTimer(30);
        const interval = setInterval(() => {
            setResendTimer(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    /* ── Step 2: Verify OTP ── */
    const handleVerifyOTP = async () => {
        if (otp.length !== 6) {
            toast.warning("Enter 6-digit OTP");
            return;
        }

        setLoading(true);
        try {
            const res: any = await api.create("/api/quick-pay/verify-otp", { phone, otp });
            if (res?.success) {
                setToken(res.token);
                setResident(res.resident);
                setBills(res.bills);
                toast.success(`Welcome ${res.resident.name}!`);
                setStep(3);
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Invalid OTP");
        } finally {
            setLoading(false);
        }
    };

    /* ── Step 3: Pay Bill ── */
    const payBill = async (bill: Bill) => {
        setPaying(true);
        try {
            toast.info("Creating order...");
            
            const orderRes: any = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:4000"}/api/quick-pay/create-order`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({ billId: bill.id })
            }).then(r => r.json());

            if (!orderRes?.success) {
                toast.error("Failed to create order");
                setPaying(false);
                return;
            }

            // Razorpay options
            const options = {
                key: orderRes.key,
                amount: orderRes.order.amount,
                currency: orderRes.order.currency,
                order_id: orderRes.order.id,
                name: "Building Maintenance",
                description: `Bill: ${MONTH_NAMES[bill.month - 1]} ${bill.year}`,
                prefill: {
                    name: orderRes.resident.name,
                    email: orderRes.resident.email,
                    contact: orderRes.resident.phone
                },
                theme: { color: "#667eea" },
                handler: async function (response: any) {
                    try {
                        const verifyRes: any = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:4000"}/api/quick-pay/verify-payment`, {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                "Authorization": `Bearer ${token}`
                            },
                            body: JSON.stringify({
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                                billId: bill.id
                            })
                        }).then(r => r.json());

                        if (verifyRes?.success) {
                            toast.success(`🎉 Payment successful! Receipt: ${verifyRes.data.receiptNo}`, {
                                autoClose: 5000
                            });
                            
                            // Remove paid bill from list
                            setBills(prev => prev.filter(b => b.id !== bill.id));
                            
                            if (bills.length === 1) {
                                // All bills paid
                                setTimeout(() => {
                                    toast.success("All bills paid! Redirecting...");
                                    navigate("/resident/login");
                                }, 2000);
                            }
                        }
                    } catch (err: any) {
                        toast.error("Verification error");
                    } finally {
                        setPaying(false);
                    }
                },
                modal: {
                    ondismiss: function () {
                        toast.info("Payment cancelled");
                        setPaying(false);
                    }
                }
            };

            const razorpay = new (window as any).Razorpay(options);
            razorpay.open();

        } catch (err: any) {
            toast.error("Error: " + err.message);
            setPaying(false);
        }
    };

    return (
        <div style={{
            minHeight: "100vh",
            background: "radial-gradient(circle at 50% -20%, #1e293b, #0f172a, #070a12)",
            display: "flex",
            alignItems: "center",
            padding: "20px"
        }}>
            <Container>
                <Row className="justify-content-center">
                    <Col md={8} lg={6}>
                        <Card className="border-0 shadow-lg" style={{ borderRadius: "16px" }}>
                            <CardBody className="p-4">
                                {/* Header */}
                                <div className="text-center mb-4">
                                    <h3 className="fw-bold">
                                        <i className="ri-flashlight-line text-warning" />
                                        {' '}Quick Pay Bill
                                    </h3>
                                    <p className="text-muted">Pay without login - Fast & Secure</p>
                                </div>

                                {/* Progress Steps */}
                                <div className="d-flex justify-content-center mb-4">
                                    {[1, 2, 3].map(s => (
                                        <div key={s} className="d-flex align-items-center">
                                            <div style={{
                                                width: 35, height: 35, borderRadius: "50%",
                                                background: step >= s ? "linear-gradient(135deg, #667eea, #764ba2)" : "#e2e8f0",
                                                color: step >= s ? "#fff" : "#6b7280",
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                fontWeight: 700
                                            }}>
                                                {step > s ? "✓" : s}
                                            </div>
                                            {s < 3 && (
                                                <div style={{
                                                    width: 60, height: 2,
                                                    background: step > s ? "#667eea" : "#e2e8f0"
                                                }} />
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Step 1: Phone */}
                                {step === 1 && (
                                    <div>
                                        <h5 className="fw-bold mb-3">Enter Phone Number</h5>
                                        <FormGroup>
                                            <Label className="small fw-semibold">Registered Phone Number</Label>
                                            <Input
                                                type="tel"
                                                placeholder="10 digit mobile"
                                                value={phone}
                                                onChange={e => {
                                                    const v = e.target.value.replace(/\D/g, '').slice(0, 10);
                                                    setPhone(v);
                                                }}
                                                maxLength={10}
                                                style={{ height: 50, fontSize: 16 }}
                                            />
                                        </FormGroup>

                                        <Button
                                            color="primary"
                                            block
                                            size="lg"
                                            onClick={handleSendOTP}
                                            disabled={loading || phone.length !== 10}
                                            style={{
                                                background: "radial-gradient(circle at 50% -20%, #1e293b, #0f172a, #070a12)",
                                                border: "none", fontWeight: 600
                                            }}
                                        >
                                            {loading ? (
                                                <><Spinner size="sm" className="me-2" />Sending OTP...</>
                                            ) : (
                                                <><i className="ri-mail-send-line me-2" />Send OTP</>
                                            )}
                                        </Button>
                                    </div>
                                )}

                                {/* Step 2: OTP */}
                                {step === 2 && (
                                    <div>
                                        <h5 className="fw-bold mb-3">Verify OTP</h5>
                                        <div style={{
                                            background: "#eff6ff", padding: "12px",
                                            borderRadius: "8px", marginBottom: "20px",
                                            fontSize: 13, textAlign: "center"
                                        }}>
                                            📧 OTP sent to: <strong>{maskedEmail}</strong>
                                        </div>

                                        <FormGroup>
                                            <Label className="small fw-semibold">Enter 6-digit OTP</Label>
                                            <Input
                                                type="text"
                                                placeholder="------"
                                                value={otp}
                                                onChange={e => {
                                                    const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                                                    setOtp(v);
                                                }}
                                                maxLength={6}
                                                style={{
                                                    height: 50, fontSize: 24,
                                                    textAlign: "center", letterSpacing: 8
                                                }}
                                            />
                                        </FormGroup>

                                        <Button
                                            color="primary"
                                            block
                                            size="lg"
                                            onClick={handleVerifyOTP}
                                            disabled={loading || otp.length !== 6}
                                            style={{
                                                background: "radial-gradient(circle at 50% -20%, #1e293b, #0f172a, #070a12)",
                                                border: "none", fontWeight: 600
                                            }}
                                        >
                                            {loading ? (
                                                <><Spinner size="sm" className="me-2" />Verifying...</>
                                            ) : (
                                                <><i className="ri-check-line me-2" />Verify & Continue</>
                                            )}
                                        </Button>

                                        <div className="text-center mt-3">
                                            {resendTimer > 0 ? (
                                                <small className="text-muted">
                                                    Resend OTP in {resendTimer}s
                                                </small>
                                            ) : (
                                                <Button color="link" size="sm" onClick={handleSendOTP}>
                                                    Resend OTP
                                                </Button>
                                            )}
                                        </div>

                                        <Button color="link" block size="sm" onClick={() => setStep(1)}>
                                            ← Change Phone Number
                                        </Button>
                                    </div>
                                )}

                                {/* Step 3: Bills */}
                                {step === 3 && (
                                    <div>
                                        <div style={{
                                            background: "linear-gradient(135deg, #f0f4ff, #e0e7ff)",
                                            padding: "16px", borderRadius: "10px",
                                            marginBottom: "20px"
                                        }}>
                                            <h6 className="fw-bold mb-1">Hi {resident?.name}! 👋</h6>
                                            <small className="text-muted">
                                                {resident?.unitsCount} units • {bills.length} pending bills
                                            </small>
                                        </div>

                                        {bills.length > 0 ? (
                                            <>
                                                <h6 className="fw-bold mb-3">Your Pending Bills:</h6>
                                                {bills.map(bill => (
                                                    <div key={bill.id} style={{
                                                        padding: "14px",
                                                        border: bill.status === "OVERDUE" ? "2px solid #fecaca" : "1px solid #e2e8f0",
                                                        borderRadius: "10px",
                                                        marginBottom: "10px",
                                                        background: bill.status === "OVERDUE" ? "#fef2f2" : "#fff"
                                                    }}>
                                                        <Row className="align-items-center">
                                                            <Col xs={7}>
                                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                                    <Badge color={bill.status === "OVERDUE" ? "danger" : "warning"} pill style={{ fontSize: 10 }}>
                                                                        {bill.status}
                                                                    </Badge>
                                                                    <span className="fw-bold">
                                                                        {MONTH_NAMES[bill.month - 1]} {bill.year}
                                                                    </span>
                                                                </div>
                                                                <small className="text-muted">
                                                                    {bill.unitCount} units: {bill.units.map(u => u.unitNo).join(", ")}
                                                                </small>
                                                                {bill.penalty > 0 && (
                                                                    <div className="text-danger" style={{ fontSize: 11 }}>
                                                                        +₹{bill.penalty} penalty
                                                                    </div>
                                                                )}
                                                            </Col>
                                                            <Col xs={5} className="text-end">
                                                                <div className="fw-bold mb-2" style={{ fontSize: 20, color: "#16a34a" }}>
                                                                    ₹{bill.totalAmount}
                                                                </div>
                                                                <Button
                                                                    color="success"
                                                                    size="sm"
                                                                    onClick={() => payBill(bill)}
                                                                    disabled={paying}
                                                                    style={{ fontWeight: 600 }}
                                                                >
                                                                    {paying ? (
                                                                        <Spinner size="sm" />
                                                                    ) : (
                                                                        <><i className="ri-wallet-line me-1" />Pay Now</>
                                                                    )}
                                                                </Button>
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                ))}
                                            </>
                                        ) : (
                                            <div className="text-center py-4">
                                                <div style={{ fontSize: 60 }}>🎉</div>
                                                <h5 className="fw-bold text-success">All Paid!</h5>
                                                <p className="text-muted">No pending bills</p>
                                                <Button color="primary" onClick={() => navigate("/resident/login")}>
                                                    Go to Login
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Footer */}
                                <hr className="my-3" />
                                <div className="text-center">
                                    <small className="text-muted">
                                        Need full features?{' '}
                                        <a href="/resident/login" style={{ color: "#667eea", fontWeight: 600 }}>
                                            Login here
                                        </a>
                                    </small>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default QuickPay;